import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Check, Loader2, CalendarX, Clock } from "lucide-react";
import { supabase } from "@/lib/supabase";

// ============================================================================
// AppointmentChangesPanel (PORTAL-4) — customer-initiated reschedule/cancel
// requests from the portal land here on Today so a dispatcher handles them
// (re-route the tech, fill the freed slot). The customer's action is recorded
// Pegasus-side immediately (ESC stays read-only); this is the office's signal
// to action it. Confirms don't appear here (they're informational — a green ✓
// on the board). Self-hides when there's nothing to handle.
// ============================================================================

interface Change {
  id: number; custno: string; dispatch: string | null; action: string;
  source?: string | null; // 'esc' | 'pegasus' — pegasus refs are dispatch UUIDs
  appointment_date: string | null; new_preference: string | null; reason: string | null;
  created_at: string; name?: string | null;
}

function prettyName(full: string | null | undefined, fallback: string): string {
  if (!full) return fallback;
  return full.replace(/\s+\d{4,}$/, "").replace(/^([^,]+),\s*(.+)$/, "$2 $1").trim() || fallback;
}
function ago(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
const apptDay = (d: string | null) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");

export function useApptChanges() {
  return useQuery({
    queryKey: ["appt_changes_new"],
    refetchInterval: 60_000,
    queryFn: async (): Promise<Change[]> => {
      const { data, error } = await supabase
        .from("pegasus_appointment_action")
        .select("id, custno, dispatch, action, source, appointment_date, new_preference, reason, created_at")
        .eq("status", "new")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as Change[];
      const custnos = [...new Set(rows.map((r) => r.custno))];
      if (custnos.length) {
        const { data: cs } = await supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos);
        const m = new Map((cs ?? []).map((c: { custno: string; fullname: string | null }) => [c.custno, c.fullname]));
        rows.forEach((r) => { r.name = m.get(r.custno) ?? null; });
      }
      return rows;
    },
  });
}

export function AppointmentChangesPanel() {
  const { data } = useApptChanges();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<number | null>(null);
  const rows = data ?? [];
  if (rows.length === 0) return null; // self-hide

  async function markHandled(id: number) {
    setBusy(id);
    const { data: u } = await supabase.auth.getUser();
    await supabase.from("pegasus_appointment_action")
      .update({ status: "handled", handled_at: new Date().toISOString(), handled_by: u?.user?.email ?? null })
      .eq("id", id);
    await qc.invalidateQueries({ queryKey: ["appt_changes_new"] });
    setBusy(null);
  }

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
        <CalendarX className="h-3.5 w-3.5 text-amber-500" />
        Appointment Changes
        <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white tabular-nums">{rows.length}</span>
      </h3>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-amber-200 bg-amber-50/40 p-3.5 shadow-e1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/customers/${r.custno}`} className="text-sm font-semibold text-slate-900 hover:text-brand-700 hover:underline">
                    {prettyName(r.name, `#${r.custno}`)}
                  </Link>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${r.action === "cancel" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                    {r.action === "cancel" ? "Wants to CANCEL" : "Wants to RESCHEDULE"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" />{ago(r.created_at)}</span>
                </div>
                {r.dispatch && (
                  <p className="mt-1.5 text-sm text-slate-700">
                    {r.source === "pegasus" ? (
                      <>Pegasus visit{r.appointment_date ? ` — ${apptDay(r.appointment_date)}` : ""} <span className="text-slate-400">(on the customer's record)</span></>
                    ) : (
                      <>
                        Dispatch <Link to={`/dispatch/${r.dispatch}`} className="font-medium text-brand-700 hover:underline">#{r.dispatch}</Link>
                        {r.appointment_date ? ` — ${apptDay(r.appointment_date)}` : ""}
                      </>
                    )}
                  </p>
                )}
                {r.new_preference && <p className="mt-0.5 text-sm text-slate-600">New preference: <span className="font-medium text-slate-700">{r.new_preference}</span></p>}
                {r.reason && <p className="mt-0.5 text-sm text-slate-600">Reason: {r.reason}</p>}
              </div>
              <button
                type="button"
                onClick={() => markHandled(r.id)}
                disabled={busy === r.id}
                title="Mark handled once you've re-routed / updated the schedule"
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50"
              >
                {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Handled
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
